import React from 'react';
import { isEmpty } from 'lodash';

import { getClaimsTableColumnsByUser } from '~/components/ClaimsTable';
import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useOrganization from '~/components/OrganizationContext';
import type { ClaimSearchResult } from '~/components/SearchBar/ClaimSearchTypes';
import GoToClaimButton from '~/components/SearchBar/GoToClaimButton';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import CardDialog from '../CardDialog';
import OverflowArrayTextDisplayWithTooltip from '../core/OverflowArrayTextWithTooltip/OverflowArrayTextDisplayWithTooltip';
import SortableTable from '../core/Tables/SortableTable';
import { useCms } from '../hooks/useCms';

interface PaginationProps {
  page: number;
  rowsPerPage: number;
  count: number;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions: number[];
}

interface SortByColumn {
  id: string;
  order?: 'asc' | 'desc';
}

interface PaginatedClaimSearchResultsProps {
  claims: ClaimSearchResult[];
  count: number;
  onCancel: () => void;
  onClaimLinkClick?: (claim?: ClaimSearchResult) => void;
  preResultsTableComponent?: React.ReactNode;
  paginationProps: PaginationProps;
  onSortByColumn: (event: unknown, sortByColumn: SortByColumn) => void;
  hideLastTouchedDate?: boolean;
  title?: string;
  SelectComponent?: React.ComponentType<{
    claim: unknown;
    onSelect?: (claim: ClaimSearchResult) => void;
    [key: string]: unknown;
  }>;
  selectComponentProps?: Record<string, unknown>;
  filteredOutColumns?: string[];
  className?: string;
}

const PaginatedClaimSearchResults: React.FC<PaginatedClaimSearchResultsProps> = ({
  claims,
  onCancel,
  onClaimLinkClick,
  preResultsTableComponent,
  paginationProps,
  onSortByColumn,
  hideLastTouchedDate,
  title,
  SelectComponent,
  selectComponentProps,
  filteredOutColumns,
  className,
}) => {
  const { user, userOrganization } = useCms();
  const organizationPreferences = useOrganization();
  const { lobConfigurationsDict } = useLobConfiguration();
  const isCommercialPoliciesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.COMMERCIAL_POLICIES
  );
  const isConfigurableFnolEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);
  const { isOperational: isClaimStageOperational } = useClaimStageConfigurations();

  let columnData = [
    ...getClaimsTableColumnsByUser({
      user,
      organization: userOrganization,
      trackingInfo: 'searchResults',
      onClaimLinkClick,
      organizationPreferences,
      lobConfigurationsDict,
      disableSortColumnsList: ['insured_contact_full_name'],
      addClaimStageColumn: isClaimStageOperational,
      claimStageViewOnly: true,
    }),
    {
      id: 'select_button',
      numeric: false,
      label: '',
      specialCell: (row: ClaimSearchResult) =>
        SelectComponent ? (
          <SelectComponent claim={row} onSelect={onClaimLinkClick} {...(selectComponentProps || {})} />
        ) : (
          <GoToClaimButton claim={row} onSelect={onClaimLinkClick} />
        ),
      disableSort: true,
    },
  ];
  if (hideLastTouchedDate) {
    columnData = columnData.filter((column) => column.id !== 'last_touched');
  }

  if (filteredOutColumns) {
    columnData = columnData.filter((column) => !filteredOutColumns.includes(column.id));
  }
  if (isConfigurableFnolEnabled && isCommercialPoliciesEnabled) {
    const firstPartyInvolvedEntities = {
      id: 'first_party_name_chip',
      label: 'First Party Involved',
      specialCell: (row: ClaimSearchResult) => {
        if (isEmpty(row?.first_party_involved_entities_display_names)) {
          return null;
        }

        return (
          <div>
            <OverflowArrayTextDisplayWithTooltip
              value={row.first_party_involved_entities_display_names}
              renderItem={(label) => (
                <span key={label}>
                  <Chip label={label} size="small" className="mr-12" />
                </span>
              )}
            />
          </div>
        );
      },
      disableSort: true,
    } as unknown as (typeof columnData)[0];

    columnData.splice(1, 0, firstPartyInvolvedEntities);
  }
  return (
    <CardDialog
      isDialog
      title={title || 'Claims'}
      onClose={onCancel}
      maxWidth={isCommercialPoliciesEnabled ? 'lg' : false}
      fullWidth
      className={className}
    >
      {preResultsTableComponent}
      <SortableTable
        columns={columnData}
        rows={claims}
        paginationProps={paginationProps}
        onSortByColumn={onSortByColumn}
      />
    </CardDialog>
  );
};

export default PaginatedClaimSearchResults;
